// Shared front-end date formatting. Every timestamp the API returns is an RFC-3339 UTC
// string; the SPA renders it in the operator's local timezone so audit trails, history
// stamps, and "last updated" columns line up with the wall clock the operator is reading
// the dashboard from. Centralised here so the format and timezone handling stay consistent
// across views — change once, every display updates.

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

// "yyyy-MM-dd HH:mm:ss" in local time. Used where the second-level precision matters
// (audit log, "updated by ... at ...").
export function formatLocalDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// "yyyy-MM-dd HH:mm" in local time. Tighter form for history lists and other compact
// contexts where seconds add noise without value.
export function formatLocalDateTimeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// "5s ago" / "12m ago" / "3h ago" / "2d ago". Falls back to empty string for unparseable
// inputs (callers usually pair this with formatLocalDateTime so the absolute form remains
// visible).
export function relativeAge(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
